'use client';

/**
 * CTO Command Center - Main Dashboard
 * Enterprise architecture suite with unified entity repository
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import { useWorkspace } from '@/lib/archimate/WorkspaceContext';
import { ENTITY_TYPE_LABELS } from '@/lib/command-center/types';
import type { EntityType } from '@/lib/command-center/types';
import { Dashboard } from '@/components/command-center/Dashboard';
import ExportPanel from '@/components/command-center/ExportPanel';

// Icons
const EntityIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const AssessmentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LinkIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

const MatchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

// Stat Card Component
function StatCard({
  title,
  value,
  icon,
  href,
  description,
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  href?: string;
  description?: string;
}) {
  const content = (
    <div className="bg-surface/50 border border-border-muted rounded-lg p-5 hover:border-border transition-colors">
      <div className="flex items-center justify-between mb-2">
        <span className="text-foreground-tertiary text-sm">{title}</span>
        <span className="text-accent">{icon}</span>
      </div>
      <div className="text-3xl font-bold text-foreground">{value}</div>
      {description && (
        <p className="text-foreground-tertiary text-sm mt-1">{description}</p>
      )}
      {href && (
        <div className="flex items-center text-accent text-sm mt-3">
          <span>View all</span>
          <ArrowRightIcon />
        </div>
      )}
    </div>
  );

  if (href) {
    return <Link href={href}>{content}</Link>;
  }
  return content;
}

// Entity Type Distribution
function EntityTypeDistribution({
  data,
}: {
  data: { entity_type: EntityType; count: number }[];
}) {
  if (!data || data.length === 0) {
    return (
      <div className="text-foreground-tertiary text-center py-8">
        No entities yet. Create your first entity to get started.
      </div>
    );
  }

  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="space-y-3">
      {data.map(({ entity_type, count }) => (
        <div key={entity_type} className="flex items-center gap-3">
          <div className="w-32 text-foreground-secondary text-sm truncate">
            {ENTITY_TYPE_LABELS[entity_type]}
          </div>
          <div className="flex-1 h-2 bg-background-dark rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all"
              style={{ width: `${(count / total) * 100}%` }}
            />
          </div>
          <div className="w-8 text-foreground-tertiary text-sm text-right">{count}</div>
        </div>
      ))}
    </div>
  );
}

// Quick Actions
function QuickActions({ onExportClick }: { onExportClick: () => void }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
      <Link
        href="/command-center/entities3?action=create"
        className="bg-surface/50 border border-border-muted rounded-lg p-4 text-center hover:border-border-muted0 transition-colors"
      >
        <div className="text-accent text-2xl mb-2">+</div>
        <div className="text-foreground text-sm">Add Entity</div>
      </Link>
      <Link
        href="/command-center/import"
        className="bg-surface/50 border border-border-muted rounded-lg p-4 text-center hover:border-border-muted0 transition-colors"
      >
        <div className="text-accent text-2xl mb-2">
          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </div>
        <div className="text-foreground text-sm">Import</div>
      </Link>
      <button
        onClick={onExportClick}
        className="bg-surface/50 border border-border-muted rounded-lg p-4 text-center hover:border-border-muted0 transition-colors"
      >
        <div className="text-accent text-2xl mb-2">
          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </div>
        <div className="text-foreground text-sm">Export</div>
      </button>
      <Link
        href="/tools"
        className="bg-surface/50 border border-border-muted rounded-lg p-4 text-center hover:border-border-muted0 transition-colors"
      >
        <div className="text-accent text-2xl mb-2">
          <AssessmentIcon />
        </div>
        <div className="text-foreground text-sm">Run Assessment</div>
      </Link>
      <Link
        href="/command-center/relationships"
        className="bg-surface/50 border border-border-muted rounded-lg p-4 text-center hover:border-border-muted0 transition-colors"
      >
        <div className="text-accent text-2xl mb-2">
          <LinkIcon />
        </div>
        <div className="text-foreground text-sm">View Map</div>
      </Link>
      <Link
        href="/tools/archimate"
        className="bg-surface/50 border border-border-muted rounded-lg p-4 text-center hover:border-border-muted0 transition-colors"
      >
        <div className="text-accent text-2xl mb-2">
          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
        </div>
        <div className="text-foreground text-sm">ArchiMate</div>
      </Link>
    </div>
  );
}

// No Workspace Selected - allows creating/selecting workspace directly
function NoWorkspaceSelected({
  workspaces,
  loading,
  onSelectWorkspace,
  onCreateWorkspace,
}: {
  workspaces: { id: string; name: string }[];
  loading: boolean;
  onSelectWorkspace: (id: string) => void;
  onCreateWorkspace: (name: string) => void;
}) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!newWorkspaceName.trim()) return;
    setCreating(true);
    try {
      await onCreateWorkspace(newWorkspaceName.trim());
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center max-w-lg">
        <div className="text-accent text-6xl mb-6">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-4">Welcome to CTO Command Center</h2>
        <p className="text-foreground-secondary mb-6">
          Create a workspace to organize your entities, assessments, and architecture across all tools.
        </p>

        {loading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        ) : (
          <>
            {/* Existing workspaces */}
            {workspaces.length > 0 && (
              <div className="mb-6">
                <h3 className="text-foreground-tertiary text-sm mb-3">Select an existing workspace:</h3>
                <div className="space-y-2">
                  {workspaces.map((ws) => (
                    <button
                      key={ws.id}
                      onClick={() => onSelectWorkspace(ws.id)}
                      className="w-full bg-surface/50 border border-border-muted rounded-lg px-4 py-3 text-left hover:border-border-muted0 transition-colors"
                    >
                      <span className="text-foreground">{ws.name}</span>
                    </button>
                  ))}
                </div>
                <div className="my-4 flex items-center gap-4">
                  <div className="flex-1 h-px bg-cream/10"></div>
                  <span className="text-foreground-muted text-sm">or</span>
                  <div className="flex-1 h-px bg-cream/10"></div>
                </div>
              </div>
            )}

            {/* Create new workspace */}
            {showCreateForm ? (
              <div className="bg-surface/30 border border-border-muted rounded-lg p-4">
                <input
                  type="text"
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  placeholder="Workspace name (e.g., My Company Architecture)"
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder-foreground-muted focus:border-accent focus:outline-none mb-3"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleCreate}
                    disabled={creating || !newWorkspaceName.trim()}
                    className="flex-1 bg-accent text-accent-foreground px-4 py-2 rounded-lg font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creating ? 'Creating...' : 'Create Workspace'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewWorkspaceName('');
                    }}
                    className="px-4 py-2 border border-border rounded-lg text-foreground-secondary hover:border-border/40 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-colors"
              >
                <span>+</span>
                Create New Workspace
              </button>
            )}

            {/* Optional ArchiMate link */}
            <p className="mt-6 text-foreground-muted text-sm">
              Want to visualize your architecture?{' '}
              <Link href="/tools/archimate" className="text-accent hover:underline">
                Open ArchiMate
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// Main Dashboard Component
export default function CommandCenterDashboard() {
  const {
    currentWorkspace,
    workspaces,
    loading: workspaceLoading,
    switchWorkspace,
    createNewWorkspace,
  } = useWorkspace();
  const { stats, loading, error, setWorkspaceId, matchSuggestions, entities } = useCommandCenter();

  // Set workspace ID when ArchiMate workspace changes
  useEffect(() => {
    if (currentWorkspace?.id) {
      setWorkspaceId(currentWorkspace.id);
    }
  }, [currentWorkspace?.id, setWorkspaceId]);

  // Handle workspace selection
  const handleSelectWorkspace = async (id: string) => {
    await switchWorkspace(id);
  };

  // Handle workspace creation
  const handleCreateWorkspace = async (name: string) => {
    await createNewWorkspace({ name, type: 'local' });
  };

  // Show no workspace message if not connected
  if (!currentWorkspace) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <NoWorkspaceSelected
            workspaces={workspaces}
            loading={workspaceLoading}
            onSelectWorkspace={handleSelectWorkspace}
            onCreateWorkspace={handleCreateWorkspace}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">CTO Command Center</h1>
            <p className="text-foreground-tertiary mt-1">
              Workspace: <span className="text-accent">{currentWorkspace.name}</span>
            </p>
          </div>
          <Link
            href="/command-center/entities3?action=create"
            className="bg-accent text-accent-foreground px-4 py-2 rounded-lg font-semibold hover:bg-accent-hover transition-colors flex items-center gap-2"
          >
            <span>+</span>
            <span>New Entity</span>
          </Link>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/50 border border-red-500/50 rounded-lg p-4 mb-6">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        )}

        {/* Dashboard Content */}
        {!loading && <Dashboard />}
      </div>
    </div>
  );
}
